import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import theme, { Theme } from 'helpers/theme';
import { createFragmentContainer, graphql } from 'react-relay';
import { Router, useRouter } from 'found';
import PageTitle from 'components/PageTitle';
import Table, { RowCell } from 'components/Table';
import useTableSearch from 'components/Table/useTableSearch';
import { useActionMenuDispatch } from 'components/ActionMenu/ActionMenuProvider';
import FilterOptions from 'components/Audits/FilterOptions';
import { Audits_audits } from './__generated__/Audits_audits.graphql';
import { Audits_auditTemplates } from './__generated__/Audits_auditTemplates.graphql';
import moment from 'moment';
import Button from 'components/Button/Button';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '40px 40px'
  },
  delegateName: {
    marginLeft: 15
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    textWrap: 'nowrap'
  },
  rowText: {
    fontSize: 13,
    textWrap: 'nowrap',
    fontWeight: 400
  },
  dateCell: {},
  dateFrom: {
    color: '#6A728F',
    fontSize: 11
  },
  spacer: {
    marginBottom: 60
  }
}));

export const DEFAULT_LIMIT = 10;

type Props = { audits: Audits_audits; auditTemplates: Audits_auditTemplates };

const Audits = ({ audits, auditTemplates }: Props) => {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const { router, match } = useRouter();
  const action = useActionMenuDispatch();
  const [cachedAudits, setCachedAudits] = React.useState(audits);

  React.useEffect(() => {
    if (audits != null) {
      setCachedAudits(audits);
    }
  }, [audits]);

  const formatRow: RowCell<Audits_audits['edges'][number]>[] = [
    {
      header: 'Audit Type',
      width: '100%',
      format: (audit) => {
        return <div className={classes.title}>{audit.auditTemplate.name}</div>;
      }
    },
    {
      header: 'Date',
      minWidth: 146,
      format: (audit) => {
        return (
          <div className={classes.dateCell}>
            <div className={classes.rowText}>
              {audit.auditSent
                ? moment(audit?.auditSent).format('DD/MM/YYYY')
                : ''}
            </div>
            <div className={classes.dateFrom}>
              {audit.auditSent ? moment(audit?.auditSent).fromNow() : ''}
            </div>
          </div>
        );
      }
    },
    {
      header: 'Auditor',
      minWidth: 160,
      format: (audit) => {
        return (
          <div className={classes.rowText}>
            {audit.auditor.firstName} {audit.auditor.lastName}
          </div>
        );
      }
    },
    {
      header: 'Download',
      minWidth: 160,
      format: (audit) => {
        return (
          <>
            {audit.pdfUrl ? (
              <Button
                archetype="flat"
                onClick={() => {
                  window.open(audit.pdfUrl ?? undefined, '_blank');
                }}
              >
                View Audit
              </Button>
            ) : (
              <div className={classes.rowText}>Not available</div>
            )}
          </>
        );
      }
    }
  ];

  const pageUrl = (
    page?: { offset: number; limit: number },
    searchQuery?: string | null,
    filter?: {
      auditTypeUUIDs?: string[];
    }
  ) => {
    if (!searchQuery) {
      if (searchQuery === '') {
        searchQuery = '';
      } else {
        searchQuery = match.location.query.search ?? '';
      }
    }

    if (!page) {
      if (match.location.query.offset) {
        page = {
          offset: parseInt(match.location.query.offset),
          limit: parseInt(match.location.query.limit)
        };
      }
    }

    if (!filter) {
      filter = {};

      if (match.location.query.auditTemplates) {
        filter.auditTypeUUIDs = match.location.query.auditTemplates.split(',');
      }
    }

    let url = `/app/audits?`;

    if (page) {
      url += `offset=${page.offset}&limit=${page.limit}&`;
    }

    if (searchQuery) {
      url += `search=${searchQuery}&`;
    }

    if (filter) {
      if (filter.auditTypeUUIDs) {
        url += `auditTemplates=${filter.auditTypeUUIDs.join(',')}&`;
      }
    }

    return url;
  };

  const onNextPage = () => {
    let { offset, limit, total } = audits.pageInfo;

    if (offset + limit >= total) {
      return;
    }

    router.push(pageUrl({ offset: Math.min(offset + limit, total), limit }));
  };

  const onSearch = useTableSearch((query: string) =>
    pageUrl({ offset: 0, limit: DEFAULT_LIMIT }, query)
  );

  const onPreviousPage = () => {
    let { offset, limit } = audits.pageInfo;
    router.push(pageUrl({ offset: Math.max(offset - limit, 0), limit }));
  };

  const onFilterClick = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    action({
      type: 'show',
      position: {
        x: evt.clientX,
        y: evt.clientY,
        placement: 'bottom-left'
      },
      children: (
        <FilterOptions
          auditTypes={auditTemplates.edges?.map((edge) => ({
            uuid: edge?.uuid ?? '',
            name: edge?.name ?? ''
          }))}
          state={{
            auditTypeUUIDs: match.location.query.auditTemplates
              ? match.location.query.auditTemplates.split(',')
              : []
          }}
          onChange={(state) => {
            router.push(
              pageUrl({ offset: 0, limit: DEFAULT_LIMIT }, undefined, state)
            );
          }}
        />
      )
    });
  };

  return (
    <div className={classes.root}>
      <PageTitle
        title="Audits"
        subTitle="View your audits conducted by the TTC Team."
      />
      <div className={classes.spacer} />
      <Table<Audits_audits['edges'][number]>
        tableName="audit types"
        data={cachedAudits?.edges ?? []}
        formatRow={formatRow}
        onNextPage={onNextPage}
        onPreviousPage={onPreviousPage}
        pageInfo={cachedAudits?.pageInfo}
        onFilterClick={onFilterClick}
        emptyTableText="To request an audit, please contact the TTC Team."
        filterActive={match.location.query?.auditTemplates?.length > 0}
        onSearch={onSearch}
        searchValue={match.location.query.search}
      />
    </div>
  );
};

const AuditsFrag = createFragmentContainer(Audits, {
  audits: graphql`
    fragment Audits_audits on AuditPage {
      edges {
        uuid
        auditSent
        auditTemplate {
          name
          uuid
        }
        auditor {
          firstName
          lastName
        }
        pdfUrl
      }
      pageInfo {
        total
        offset
        limit
        given
      }
    }
  `,
  auditTemplates: graphql`
    fragment Audits_auditTemplates on AuditTemplatePage {
      edges {
        uuid
        name
        auditType
      }
      pageInfo {
        total
        offset
        limit
        given
      }
    }
  `
});

export default AuditsFrag;
